import {
  Bath,
  BookOpen,
  ChartGantt,
  Database,
  FlaskConical,
  Gamepad2,
  Ghost,
  Hammer,
  type LucideIcon,
  Microscope,
  Receipt,
  ShieldAlert,
  Timer,
  Trophy,
  Users,
} from "lucide-react";

export type AdminTabValue =
  | "internal-monitors"
  | "hall-of-flames"
  | "the-floor"
  | "overhead"
  | "shadow-proofs"
  | "proof-traces"
  | "sandbox-hottubs"
  | "proof-knowledge"
  | "users"
  | "lago"
  | "data-catalogs"
  | "fusion-lab"
  | "codesmith-flappy";

export type AdminTab = {
  value: AdminTabValue;
  label: string;
  icon: LucideIcon;
};

/** A section with sub-tabs is a hover-card group in AdminSidebar, like Run History in the GitHub Actions sidebar. */
export type AdminSection =
  | AdminTab
  | {
      /** `?tab=` value that resolves to the first sub-tab. */
      param: string;
      label: string;
      icon: LucideIcon;
      subTabs: readonly AdminTab[];
    };

export const ADMIN_SECTIONS: readonly AdminSection[] = [
  {
    value: "internal-monitors",
    label: "Internal Monitors",
    icon: ShieldAlert,
  },
  { value: "hall-of-flames", label: "Hall of Flames", icon: Trophy },
  { value: "the-floor", label: "The Floor", icon: Hammer },
  { value: "overhead", label: "Overhead", icon: Timer },
  {
    param: "proof-analysis",
    label: "Proof Analysis",
    icon: Microscope,
    subTabs: [
      { value: "shadow-proofs", label: "Shadow Proofs", icon: Ghost },
      { value: "proof-traces", label: "Proof Traces", icon: ChartGantt },
      { value: "sandbox-hottubs", label: "Sandbox Hottubs", icon: Bath },
      { value: "proof-knowledge", label: "Proof Knowledge", icon: BookOpen },
    ],
  },
  { value: "users", label: "Users", icon: Users },
  { value: "lago", label: "Lago", icon: Receipt },
  { value: "data-catalogs", label: "Data Catalogs", icon: Database },
  { value: "fusion-lab", label: "Fusion Lab", icon: FlaskConical },
  {
    value: "codesmith-flappy",
    label: "Flappy [code]smith",
    icon: Gamepad2,
  },
];

const ADMIN_TABS: readonly AdminTab[] = ADMIN_SECTIONS.flatMap((section) =>
  "subTabs" in section ? section.subTabs : [section],
);

const DEFAULT_TAB = ADMIN_TABS[0];

/** `?tab=` value → its tab, falling back to the first for missing or unknown values. */
export function adminTabFromParam(param: string | null): AdminTab {
  const group = ADMIN_SECTIONS.find(
    (section) => "subTabs" in section && section.param === param,
  );
  if (group && "subTabs" in group) return group.subTabs[0];
  return ADMIN_TABS.find((tab) => tab.value === param) ?? DEFAULT_TAB;
}

export function adminTabHref(value: AdminTabValue): string {
  return value === DEFAULT_TAB.value ? "/admin" : `/admin?tab=${value}`;
}
